/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser'

// Access runtime config if available, fall back to env variables
const runtimeConfig = (window as any).REACT_APP_CONFIG || {}

// Helper function to extract string value from potentially JSON-formatted values
const extractStringValue = (value: string | undefined): string => {
  if (!value) return ''

  // Try to parse as JSON if it looks like JSON
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      // If it's an object with a value property, use that
      if (parsed && typeof parsed === 'object' && parsed.value) {
        return parsed.value.toString()
      } else if (parsed && typeof parsed === 'string') {
        // If the parsed result is a string, use that
        return parsed
      } else {
        // Otherwise try to use the first value found as a string
        const firstValue = Object.values(parsed)[0]
        if (firstValue && typeof firstValue === 'string') {
          return firstValue
        }
      }
    } catch (e) {
      // Not valid JSON or other parsing error, keep original
    }
  }

  // Remove any surrounding quotes
  if (value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1)
  }

  return value
}

// Helper function to get configuration values with type safety and sanitization
const getConfig = (key: string, fallback: string = ''): string => {
  const runtimeValue = runtimeConfig[key]
  const envValue = process.env[key]

  console.log(`Getting config for ${key}:`, {
    runtimeValue: runtimeValue ? 'EXISTS' : 'MISSING',
    envValue: envValue ? 'EXISTS' : 'MISSING',
  })

  // Try runtime config first
  if (typeof runtimeValue === 'string' && runtimeValue.trim() !== '') {
    return extractStringValue(runtimeValue.trim())
  }

  // Then try environment variables
  if (typeof envValue === 'string' && envValue.trim() !== '') {
    return extractStringValue(envValue.trim())
  }

  // Fall back to default
  return fallback
}

// Get required values with explicit defaults
const clientId = getConfig('REACT_APP_CLIENT_ID', '')
const tenantId = getConfig('REACT_APP_TENANT_ID', '')
const redirectUri = getConfig('REACT_APP_REDIRECT_URI', window.location.origin)
const apiClientId = getConfig('REACT_APP_API_CLIENT_ID', '')

// Apply extra validation - don't allow empty values for critical parameters
if (!clientId) {
  console.error('CLIENT ID IS MISSING OR EMPTY!')
}
if (!tenantId) {
  console.error('TENANT ID IS MISSING OR EMPTY!')
}
if (!apiClientId) {
  console.error('API CLIENT ID IS MISSING OR EMPTY!')
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          // case LogLevel.Info:
          //   console.info(message)
          //   return
          // case LogLevel.Verbose:
          //   console.debug(message)
          //   return
          case LogLevel.Warning:
            console.warn(message)
            return
          default:
            return
        }
      },
      logLevel: LogLevel.Verbose,
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
}

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
}

/**
 * Configuration for our own API authentication
 */
export const apiConfig = {
  scopes: [`api://${apiClientId}/access_as_user`],
  resource: `api://${apiClientId}`,
  authority: `https://login.microsoftonline.com/${tenantId}`,
}
